import React, { useState, useCallback } from 'react';
import { MapPin } from 'lucide-react';

// Mapbox Geocoding API for city suggestions
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;

// State name to abbreviation mapping
const STATE_ABBREVIATIONS = {
  'Alabama': 'AL', 'Alaska': 'AK', 'Arizona': 'AZ', 'Arkansas': 'AR', 'California': 'CA',
  'Colorado': 'CO', 'Connecticut': 'CT', 'Delaware': 'DE', 'Florida': 'FL', 'Georgia': 'GA',
  'Hawaii': 'HI', 'Idaho': 'ID', 'Illinois': 'IL', 'Indiana': 'IN', 'Iowa': 'IA',
  'Kansas': 'KS', 'Kentucky': 'KY', 'Louisiana': 'LA', 'Maine': 'ME', 'Maryland': 'MD',
  'Massachusetts': 'MA', 'Michigan': 'MI', 'Minnesota': 'MN', 'Mississippi': 'MS', 'Missouri': 'MO',
  'Montana': 'MT', 'Nebraska': 'NE', 'Nevada': 'NV', 'New Hampshire': 'NH', 'New Jersey': 'NJ',
  'New Mexico': 'NM', 'New York': 'NY', 'North Carolina': 'NC', 'North Dakota': 'ND', 'Ohio': 'OH',
  'Oklahoma': 'OK', 'Oregon': 'OR', 'Pennsylvania': 'PA', 'Rhode Island': 'RI', 'South Carolina': 'SC',
  'South Dakota': 'SD', 'Tennessee': 'TN', 'Texas': 'TX', 'Utah': 'UT', 'Vermont': 'VT',
  'Virginia': 'VA', 'Washington': 'WA', 'West Virginia': 'WV', 'Wisconsin': 'WI', 'Wyoming': 'WY'
};

// Function to get city suggestions from Mapbox Geocoding API
export const getMapboxCitySuggestions = async (query) => {
  if (!query || query.length < 2) return [];
  
  if (!MAPBOX_ACCESS_TOKEN) {
    console.warn('[CityAutocomplete] Mapbox token not available');
    return [];
  }
  
  try {
    const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${MAPBOX_ACCESS_TOKEN}&country=US&types=place&limit=10`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Format suggestions as "City, State" with abbreviations
    const suggestions = data.features.map(feature => {
      const placeName = feature.place_name;
      const parts = placeName.split(', ');
      
      if (parts.length >= 2) {
        const city = parts[0];
        const stateFull = parts[1];
        
        // Convert full state name to abbreviation
        const stateAbbr = STATE_ABBREVIATIONS[stateFull] || stateFull;
        
        return `${city}, ${stateAbbr}`;
      }
      return placeName;
    });
    
    // Filter out suggestions without state
    return suggestions.filter(suggestion => suggestion.includes(', '));
    
  } catch (error) {
    console.error('[CityAutocomplete] Error fetching suggestions:', error);
    return [];
  }
};

/**
 * CityAutocomplete Component
 * 
 * Reusable city/state autocomplete input field using Mapbox Geocoding API
 * Used in Lane Pricing and Route Optimization for origin/destination inputs
 * 
 * @param {string} value - Current input value
 * @param {function} onChange - Function to call when value changes
 * @param {string} placeholder - Placeholder text
 * @param {string} label - Label text
 * @param {string} id - Input ID
 * @param {string} name - Input name
 * @param {boolean} required - Whether field is required
 * @param {string} className - Additional CSS classes for input
 * @param {boolean} showIcon - Whether to show MapPin icon
 */
function CityAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Chicago, IL",
  label,
  id,
  name,
  required = false,
  className = "",
  showIcon = true
}) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Handle input change with Mapbox suggestions
  const handleInputChange = useCallback(async (e) => {
    const newValue = e.target.value;
    onChange(e); // Call parent onChange
    
    if (newValue.length >= 2) {
      setLoadingSuggestions(true);
      try {
        const suggestions = await getMapboxCitySuggestions(newValue);
        setFilteredSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.warn('[CityAutocomplete] Error getting suggestions:', error);
        setFilteredSuggestions([]);
        setShowSuggestions(false);
      } finally {
        setLoadingSuggestions(false);
      }
    } else {
      setShowSuggestions(false);
      setFilteredSuggestions([]);
      setLoadingSuggestions(false);
    }
  }, [onChange]);

  // Handle suggestion selection
  const handleSuggestionSelect = useCallback((suggestion) => {
    // Create synthetic event object for onChange
    const syntheticEvent = {
      target: {
        name: name,
        value: suggestion
      }
    };
    onChange(syntheticEvent);
    setShowSuggestions(false);
    setFilteredSuggestions([]);
  }, [name, onChange]);

  // Handle input focus - trigger suggestions if there's text
  const handleFocus = useCallback(async () => {
    if (value && value.length >= 2) {
      setLoadingSuggestions(true);
      try {
        const suggestions = await getMapboxCitySuggestions(value);
        setFilteredSuggestions(suggestions);
        setShowSuggestions(suggestions.length > 0);
      } catch (error) {
        console.warn('[CityAutocomplete] Error getting suggestions on focus:', error);
      } finally {
        setLoadingSuggestions(false);
      }
    }
  }, [value]);

  // Handle input blur (with delay to allow clicking suggestions)
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  }, []);

  return (
    <div className="relative">
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        {showIcon && (
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
        )}
        <input
          type="text"
          id={id}
          name={name}
          value={value}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          className={`${showIcon ? 'pl-10' : ''} ${className}`}
          placeholder={placeholder}
          required={required}
          autoComplete="off"
        />
      </div>

      {/* Invisible backdrop while suggestions are open. The dropdown below is
          absolutely positioned and can visually overlap other form fields
          (e.g. a "Cost Per Mile" input directly beneath the origin field).
          Without this, a click aimed at that other field can silently land
          on a stale suggestion instead and swap in the wrong city with no
          indication anything happened. The backdrop absorbs that click and
          just closes the dropdown - the user's next click reaches the field
          they actually meant to hit. */}
      {(showSuggestions || loadingSuggestions) && (
        <div
          className="fixed inset-0 z-40"
          onMouseDown={() => {
            setShowSuggestions(false);
            setFilteredSuggestions([]);
          }}
        />
      )}

      {/* City Suggestions Dropdown */}
      {(showSuggestions || loadingSuggestions) && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
          {loadingSuggestions ? (
            <div className="px-3 py-2 text-gray-500 dark:text-gray-400 flex items-center space-x-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
              <span>Loading cities...</span>
            </div>
          ) : (
            filteredSuggestions.map((suggestion, index) => (
              <div
                key={index}
                onMouseDown={(e) => {
                  e.preventDefault(); // Prevent blur from firing
                  handleSuggestionSelect(suggestion);
                }}
                onClick={() => handleSuggestionSelect(suggestion)}
                className="px-3 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600 text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <span>{suggestion}</span>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default CityAutocomplete;